import { dateTimeText, viewerTimeZone } from '../../../../shared/date/local-time'
import { environmentOf } from '../../../../shared/env/environment'
import { STATUS_LABEL, groupTitle, recurredText } from './errors-format'

// "Copy for triage" (owner 2026-10-23: "a details with a copy that gives me
// something I can paste to an agent like you easily for triage"): one
// Markdown block with what the error page shows, for an engineer or an agent.
//
// What it never holds: tokens, cookies, emails or personal names. People are
// a count only; who changed the status, the note and the links' labels are
// left out; and every free text (message, stacks, page path) goes through
// scrubText as defence in depth, on top of the server's own redaction.
//
// It stays under MAX_TRIAGE characters: the component stack is trimmed
// first, then the stack, then the message, each from the bottom with
// "[trimmed]" where the cut is.

export const MAX_TRIAGE = 4000
export const TRIMMED = '[trimmed]'

// Masks what must never leave in a paste: email addresses, JWTs, bearer and
// basic credentials, cookie headers, and secret-looking query or key=value
// pairs.
export const scrubText = value => String(value == null ? '' : value)
  .replace(/^(\s*(?:set-)?cookie\s*:).*$/gim, '$1 [redacted]')
  .replace(/\b(bearer|basic)\s+[A-Za-z0-9._~+/=-]{8,}/gi, '$1 [redacted]')
  .replace(/\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}(?:\.[A-Za-z0-9_-]*)?/g, '[token]')
  .replace(/\b((?:access_?|refresh_?|id_?)?token|password|passwd|secret|api_?key|key|session(?:_?id)?|sid|auth(?:orization)?|cookie|signature|sig)=([^\s&"'<>]+)/gi, '$1=[redacted]')
  // a sign-in code or launch token in a URL (short values, like a SQLSTATE code=23505, stay)
  .replace(/\b(code|lt|state)=([A-Za-z0-9._~-]{16,})/gi, '$1=[redacted]')
  .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[email]')

// A time as UTC ISO (seconds) and in the viewer's own zone.
const isoText = value => {
  const t = Date.parse(value)
  return Number.isFinite(t) ? new Date(t).toISOString().replace(/\.\d{3}Z$/, 'Z') : ''
}
export const whenText = (value, tz) => {
  const iso = isoText(value)
  if (!iso) return 'unknown'
  return `${iso} (${dateTimeText(value, { tz })})`
}

// A fenced block that no backtick run in the text can close early.
const fence = (text, lang = 'text') => {
  const runs = String(text).match(/`+/g) || []
  const longest = runs.reduce((n, r) => Math.max(n, r.length), 0)
  const ticks = '`'.repeat(Math.max(3, longest + 1))
  return `${ticks}${lang}\n${text}\n${ticks}`
}

// Inline code, or '' for nothing.
const code = value => {
  const s = String(value == null ? '' : value).replace(/\s+/g, ' ').trim()
  if (!s) return ''
  return s.includes('`') ? `\`\` ${s} \`\`` : `\`${s}\``
}

// Whole lines from the top within `max` characters, then "[trimmed]".
export const cutLines = (text, max) => {
  const s = String(text || '')
  if (s.length <= max) return s
  const room = max - TRIMMED.length - 1
  if (room <= 0) return TRIMMED
  let out = ''
  for (const line of s.split('\n')) {
    const next = out ? `${out}\n${line}` : line
    if (next.length > room) break
    out = next
  }
  if (!out) out = s.slice(0, room)
  return `${out}\n${TRIMMED}`
}

// A stack as shown: its leading blank lines and trailing space dropped, the
// first frame's indent kept.
const blockText = value => String(value || '').replace(/^(?:[ \t]*\r?\n)+/, '').trimEnd()

const SOURCE_WORD = { request: 'server', job: 'job', boot: 'boot', client: 'browser' }

// Where it happens, after the source: a request's method and route, a job's name.
const whereText = g => {
  if (g.source === 'request') return [g.method, g.route].filter(Boolean).join(' ')
  if (g.source === 'job') return g.jobName || ''
  return ''
}

const statusText = g => {
  const label = STATUS_LABEL[g.status] || g.status || 'unknown'
  const since = g.statusChangedAt && g.status !== 'open' ? ` since ${isoText(g.statusChangedAt)}` : ''
  return `${label.toLowerCase()}${since}`
}

const recurredLine = g => {
  const n = Number(g.cameBackCount) || 0
  if (!n) return 'never'
  const last = g.cameBackAt ? `, last ${isoText(g.cameBackAt)}` : ''
  return `${recurredText(n)}${last}`
}

const requestLogHref = (origin, id, at) =>
  `${origin}/admin/request-log/${encodeURIComponent(id)}${at ? `?at=${encodeURIComponent(at)}` : ''}`

const item = (label, value) => (value === '' || value === null || value === undefined ? null : `- ${label}: ${value}`)

// The triage block for one error group `g` and its newest sample `latest`
// (the first of the page's occurrences, or null while they load).
//   origin: the admin's origin ("https://admin.educationxr.com")
//   host:   its host name (the environment is read from it)
//   tz:     the viewer's time zone, for the local times
export const triageText = ({ g, latest, origin = '', host = '', tz = viewerTimeZone() }) => {
  if (!g) return ''
  const o = latest || null
  const env = environmentOf(host)
  const title = scrubText(groupTitle(g))
  const from = (o && o.from) || null
  const isClient = g.source === 'client'

  const requestID = (o && o.requestID) || g.latestRequestID || ''
  const requestAt = (o && o.requestID && o.occurredAt) || g.latestOccurredAt || ''

  const parts = {
    message: scrubText(String(g.sampleMessage || '').trim()),
    stack: scrubText(blockText(o && o.stack)),
    componentStack: scrubText(blockText(o && o.componentStack))
  }

  const build = ({ message, stack, componentStack }) => {
    const lines = [
      `## Error triage: ${title}`,
      '',
      item('Error page', `${origin}/admin/errors/${g.id}`),
      item('Environment', `${env}${host ? ` (${String(host).toLowerCase()})` : ''}`),
      item('Type', code(g.errorType)),
      item('Service', code(g.service)),
      item('Source', [SOURCE_WORD[g.source] || g.source, whereText(g)].filter(Boolean).join(' · ')),
      item('Status', statusText(g)),
      item('Recurred after resolve', recurredLine(g)),
      item('Events', `${Number(g.totalCount) || 0} total, ${Number(g.last24h) || 0} in the last 24 hours`),
      item('People affected', String(Number(g.peopleCount) || 0)),
      item('First seen', whenText(g.firstSeenAt, tz)),
      item('Last seen', whenText(g.lastSeenAt, tz)),
      '',
      '### Message',
      fence(message || '(no message)'),
      ''
    ]

    const where = [
      item('Function', code(g.location)),
      item('File', code(g.locationLine)),
      o && o.locationLine && o.locationLine !== g.locationLine ? item('Sample file', code(o.locationLine)) : null
    ].filter(Boolean)
    if (where.length) lines.push('### Location', ...where, '')

    const sample = [
      o ? item('When', whenText(o.occurredAt, tz)) : null,
      o && o.message && o.message !== g.sampleMessage ? item('Message', code(cutLines(scrubText(o.message).replace(/\s+/g, ' '), 300))) : null,
      item('Request id', code(requestID)),
      o ? item('Method', code(o.method)) : null,
      o ? item('Route', code(o.route)) : null,
      o && o.status ? item('HTTP status', String(o.status)) : null,
      o ? item('Service', code(o.service)) : null,
      o ? item('Host', code(o.host)) : null,
      o && o.jobRunID ? item('Job run', String(o.jobRunID)) : null,
      o && o.jobError ? item('Job error', code(scrubText(o.jobError))) : null,
      requestID ? item('Request log', requestLogHref(origin, requestID, requestAt)) : null,
      !isClient && from && from.display ? item('From', from.display) : null
    ].filter(Boolean)
    if (sample.length) lines.push('### Latest sample', ...sample, '')

    if (isClient) {
      const app = from && from.app ? [from.app, from.appVersion].filter(Boolean).join(' ') : ''
      const browser = from && (from.browser || from.platform)
      const client = [
        o && o.pagePath ? item('Page', code(scrubText(o.pagePath))) : null,
        app ? item('App', app) : null,
        browser ? item('Browser', browser) : null,
        o && o.failedRequestID ? item('After a failed request', `${code(o.failedRequestID)} ${requestLogHref(origin, o.failedRequestID, o.occurredAt)}`) : null
      ].filter(Boolean)
      if (client.length) lines.push('### Browser', ...client, '')
    }

    if (stack) lines.push('### Stack (latest sample)', fence(stack), '')
    if (componentStack) lines.push('### Component stack (latest sample)', fence(componentStack), '')

    return lines.filter(line => line !== null).join('\n').replace(/\n{3,}/g, '\n\n').trimEnd()
  }

  // Over the limit: trim the stacks first (keeping their top frames), then
  // the message; a floor on each first pass keeps some of every block.
  const passes = [['componentStack', 400], ['stack', 1200], ['componentStack', 0], ['stack', 0], ['message', 300]]
  let current = parts
  let text = build(current)
  for (const [key, floor] of passes) {
    const over = text.length - MAX_TRIAGE
    if (over <= 0) break
    const value = current[key]
    if (!value || value.length <= floor) continue
    current = { ...current, [key]: cutLines(value, Math.max(floor, value.length - over)) }
    text = build(current)
  }
  if (text.length > MAX_TRIAGE) text = `${text.slice(0, MAX_TRIAGE - TRIMMED.length - 1)}\n${TRIMMED}`
  return text
}
